import type { CSSProperties } from 'react';

import type { UserRef } from '@/shared/api/types';
import { cn } from '@/shared/lib/cn';
import { avatarHue, getInitials } from '@/shared/lib/strings';
import { usePresence } from '@/shared/realtime/presence';
import { AvatarFallback, AvatarImage, AvatarRoot } from '@/shared/ui/avatar';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE_CLASS: Record<AvatarSize, string> = {
  xs: 'size-5 text-[0.5625rem]',
  sm: 'size-6 text-[0.625rem]',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
  xl: 'size-14 text-lg',
  '2xl': 'size-20 text-2xl',
};

const DOT_CLASS: Record<AvatarSize, string> = {
  xs: 'size-1.5 ring-1',
  sm: 'size-2 ring-[1.5px]',
  md: 'size-2.5 ring-2',
  lg: 'size-3 ring-2',
  xl: 'size-3.5 ring-[3px]',
  '2xl': 'size-4 ring-[3px]',
};

export interface AvatarProps {
  /** Shorthand: takes name, image, id (color seed + live presence) and `isOnline` from a UserRef. */
  user?: Pick<UserRef, 'id' | 'fullName' | 'avatarUrl'> & { isOnline?: boolean };
  name?: string;
  src?: string | null;
  /** Seed for the initials color (defaults to user id or name). */
  seed?: string;
  size?: AvatarSize;
  /** `rounded` for groups/projects, `circle` for people. */
  shape?: 'circle' | 'rounded';
  /** Show the presence dot. For `user`, live presence from realtime overrides `user.isOnline`. */
  showPresence?: boolean;
  /** Explicit presence when `user` is not given. */
  online?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Person / group avatar: image with initials fallback tinted by a stable color hash, optional presence dot. */
export function Avatar({
  user,
  name,
  src,
  seed,
  size = 'md',
  shape = 'circle',
  showPresence = false,
  online,
  className,
  style,
}: AvatarProps) {
  const displayName = user?.fullName ?? name ?? '';
  const image = user?.avatarUrl ?? src ?? null;
  const hue = avatarHue(seed ?? user?.id ?? displayName);
  const livePresence = usePresence(user?.id, user?.isOnline ?? online);
  const isOnline = user ? livePresence : online;
  const radius =
    shape === 'circle' ? 'rounded-full' : size === 'xs' || size === 'sm' ? 'rounded-[5px]' : 'rounded-lg';

  return (
    <span
      className={cn('relative inline-flex size-fit shrink-0', className)}
      style={style}
      data-slot="user-avatar"
    >
      <AvatarRoot className={cn(SIZE_CLASS[size], radius)}>
        {image && <AvatarImage src={image} alt={displayName} className={radius} />}
        <AvatarFallback
          delayMs={image ? 400 : 0}
          className={cn('avatar-tint font-semibold tracking-tight select-none', radius)}
          style={{ '--avatar-h': hue } as CSSProperties}
          aria-label={displayName}
        >
          {getInitials(displayName)}
        </AvatarFallback>
      </AvatarRoot>
      {showPresence && isOnline !== undefined && (
        <span
          aria-hidden
          data-presence={isOnline ? 'online' : 'offline'}
          className={cn(
            'absolute right-0 bottom-0 rounded-full ring-background',
            DOT_CLASS[size],
            isOnline ? 'bg-success' : 'bg-border-strong',
          )}
        />
      )}
    </span>
  );
}
